import { select, event, selectAll } from 'd3-selection';
import CanvasObject from './CanvasObject';
import CanvasPath from './CanvasPath';
import CanvasObjectList from './CanvasObjectList';
import LayerRenderer from './LayerRenderer';
import CePolygon from '../geometry/CePolygon';
import CeCircle from '../geometry/CeCircle';
import GCPolygon from '../geometry/GCPolygon';
import CeRectangle from '../geometry/CeRectangle';
import CeLine from '../geometry/CeLine';
import { sanitize } from '../utility/StringHandle';
import CanvasHollowInner from '../helper/canvasHelper/CanvasHollowInner';

var COMPONENT_COLOR = 'yellow';
var SELECTION_COLOR = 'magenta';
const PINPATH_COLOR = 'orange'

var CompLayerRenderer = function (metalLayer, layoutRenderer, layerName, hidden, clickCallback, milToUnit) {
  LayerRenderer.call(this, layoutRenderer, layerName, hidden);
  this.metalLayer = metalLayer;
  this.compObjList = [];
  this.d3CompGroup = [];
  this.compPinsData = [];
  this.clickCallback = clickCallback;
  this.milToUnit = milToUnit;
  this.point = new Map();
  this.pinOutline = '';
  this.renderSettings = {};
};

CompLayerRenderer.prototype = Object.create(LayerRenderer.prototype);
CompLayerRenderer.prototype.constructor = CompLayerRenderer;

CompLayerRenderer.prototype.setShowPinList = function (showPinList = [], onlyHiddenPins = false) {
  this.showPinList = new Set(showPinList.map(item => `${item.name}_${item.pin}`));
  this.onlyHiddenPins = onlyHiddenPins;
}

/**
 * generate component object list form drawing svg from component layer
 */
CompLayerRenderer.prototype.prepareData = function () {

  var compList = this.metalLayer.GetComponentLayer().GetComponents();
  // get the unit, it will influence the calculation of outline
  var unit = this.layoutDB.mUnits;
  if (compList == null) {
    console.log("Unable to get the component layer from metal layer.");
    return;
  }
  this.compObjList = [];
  // create a template object that contains detailed object information
  const templateObjBase = new CanvasObject();
  templateObjBase.layer = this.layerName;
  templateObjBase.type = 'pin';
  const netMap = new Map();
  for (let i = 0; i < compList.length; i++) {
    let compName = compList[i].GetName();
    let partName = sanitize(compList[i].GetPartName());
    let cePart = compList[i].mPart;
    let footPrintSymbol = cePart.GetFootPrintSymbol(this.layoutDB.mPartMgr);
    const netList = this.layoutDB.mNetManager.GetNetsByComp(compName);
    let compObj = {
      name: compName,
      svgGrp: null,
      outlineObj: null,
      objList: new CanvasObjectList(this.clickCallback),
      selected: false
    };
    this.compObjList.push(compObj);
    const templateObj = Object.create(templateObjBase);
    templateObj.comp = compName;

    // draw pads
    let pads = footPrintSymbol ? footPrintSymbol.GetPadGeometries() : [];
    let minY, minX = minY = Number.MAX_VALUE;
    let maxY, maxX = maxY = Number.MIN_VALUE;
    // console.log(footPrintSymbol, pads)
    if (pads) {
      let metalPadList = footPrintSymbol.mMetalLayerList[0].mPadList;
      const shouldFilterPins = !this.onlyHiddenPins && this.showPinList && this.showPinList.size ? true : false;
      for (const item of netList) netMap.set(item.mPinNum, item);
      for (let j = 0; j < pads.length; j++) {
        const pad = pads[j];
        const padInfo = metalPadList[j];
        const pinInfo = cePart.GetPinByPinNumber(padInfo.mPadID);

        const pinName = pinInfo.mName;
        const pinNumber = pinInfo.mNumber;

        if (!shouldFilterPins || (shouldFilterPins && this.showPinList.has(`${compName}_${pinNumber}`))) {
          const netInfo = netMap.get(pinNumber);
          const netSuffix = netInfo ? ` -> ${netInfo.net}` : '';
          const toolTip = `${compName}: ${pinNumber}:: ${pinName}${netSuffix}`;

          Object.assign(templateObj, {
            pin: pinName,
            pinNumber: pinNumber,
            toolTip: toolTip
          });

          compObj.objList.addGeometry(pad, compList[i].mLocation, templateObj, `${partName}-${padInfo.mTemplateID}`);
        }

        if (pad.constructor === GCPolygon) {
          let minPadX = Infinity, minPadY = Infinity;
          let maxPadX = -Infinity, maxPadY = -Infinity;

          for (let k = 0; k < pad.mXs.length; k++) {
            const x = pad.mXs[k];
            const y = pad.mYs[k];
            if (x < minPadX) minPadX = x;
            if (y < minPadY) minPadY = y;
            if (x > maxPadX) maxPadX = x;
            if (y > maxPadY) maxPadY = y;
          }

          minX = Math.min(minX, minPadX);
          minY = Math.min(minY, minPadY);
          maxX = Math.max(maxX, maxPadX);
          maxY = Math.max(maxY, maxPadY);
        } else if (pad.constructor === CeCircle) {
          const centerX = pad.mCenter.getX();
          const centerY = pad.mCenter.getY();
          const radius = pad.mDiameter / 2;

          minX = Math.min(minX, centerX - radius);
          minY = Math.min(minY, centerY - radius);
          maxX = Math.max(maxX, centerX + radius);
          maxY = Math.max(maxY, centerY + radius);
        } else {
          console.log(pad);
        }
      }

      netMap.clear();
    }

    // draw the outline
    // if(unit === 'mm'){
    // 	minX -= 0.2; minY-= 0.2; maxX += 0.2; maxY += 0.2;
    // }else{
    // 	minX -= 15; minY-= 15; maxX += 15; maxY += 15;
    // }

    let outline;
    if (footPrintSymbol && footPrintSymbol.mOutline) {
      outline = footPrintSymbol.mOutline.ConvertToGCPolygon();
    } else {

      outline = new CePolygon();
      if (!footPrintSymbol || !footPrintSymbol.mMetalLayerList || !footPrintSymbol.mMetalLayerList.length) {
        console.error('foot print symbol don\'t have metal layer: ' + (footPrintSymbol ? footPrintSymbol.mID : ""));

      } else {
        let logicLayers = footPrintSymbol.mMetalLayerList[0].mLogicLayers;
        if (footPrintSymbol.mMetalLayerList.length > 1) console.log(footPrintSymbol.mMetalLayerList.length)
        for (let iLayer = 0; iLayer < logicLayers.length; iLayer++) {
          //
          if (logicLayers[iLayer].GetType() != "DOCUMENT") continue;
          let geomList = logicLayers[iLayer].mGeomList.slice(0);
          //if (geomList.length < 3) console.log('geomList: '+geomList.length);
          let start, end;
          for (let iGeom = 0; iGeom < geomList.length; iGeom++) {
            let geometry = geomList[iGeom].mGeometry;
            if (geometry instanceof CeRectangle) {
              let center = geometry.mCenter;
              start = {
                mX: geometry.mCenter.mX - geometry.mWidth / 2,
                mY: geometry.mCenter.mY - geometry.mHeight / 2
              };

              end = {
                mX: geometry.mCenter.mX + geometry.mWidth / 2,
                mY: geometry.mCenter.mY + geometry.mHeight / 2
              };
            } else if (geometry instanceof CeLine) {
              start = geometry.GetStart();
              end = geometry.GetEnd();
            } else {
              console.log('error: don\'t support this geometry')
            }

            minX = Math.min(minX, start.mX, end.mX);
            minY = Math.min(minY, start.mY, end.mY);
            maxX = Math.max(maxX, start.mX, end.mX);
            maxY = Math.max(maxY, start.mY, end.mY);
          };
        }
      }
      outline.AddVertex(minX, minY);
      outline.AddVertex(minX, maxY);
      outline.AddVertex(maxX, maxY);
      outline.AddVertex(maxX, minY);
      outline = outline.ConvertToGCPolygon();
    }
    if (compList[i].mLocation) {
      compList[i].mLocation.TransformGCPolygon(outline);
      compObj.outlineObj = new CanvasPath();
      compObj.outlineObj.pathData = outline.GetSvgPath();
      compObj.outlineObj.layer = this.layerName;
      compObj.outlineObj.type = 'component';
      compObj.outlineObj.comp = compName;
    }

  } // for (var i = 0; i < compList.length; i++)
  // call the virtual function of the parent
  LayerRenderer.prototype.prepareData.call(this);
}; // CompLayerRenderer.prototype.prepareData

var clickEventHandling = function (d, layoutRenderer, callback) {
  if (layoutRenderer.canvasType !== "NET") {
    //nets canvas
    event.stopPropagation();
  }

  if (event.defaultPrevented || layoutRenderer.coupleMode) {
    return;
  }

  select(d.svgGrp).attr('display', 'none');
  var nets = getElementsByPosition(event.clientX, event.clientY);
  const _comps = getCompsByPosition(event.clientX, event.clientY);
  select(d.svgGrp).attr('display', null);
  const canvasObj = { comps: [d.name] };
  canvasObj.multi = event.ctrlKey === true;
  if (nets.length > 0) {
    canvasObj.nets = nets;
  }
  if (_comps.length > 0) {
    canvasObj.comps = [...canvasObj.comps, ..._comps];
  }
  canvasObj.x = event.clientX;
  canvasObj.y = event.clientY;
  callback && callback(canvasObj);
};

function getElementsByPosition(x, y) {
  var element = document.elementFromPoint(x, y);
  if (element.tagName !== 'svg') {
    select(element).attr('display', 'none');
    var nets = getElementsByPosition(x, y);
    select(element).attr('display', null);
    var net = element.__data__.net;
    if (net && nets.indexOf(net) < 0)
      nets.push(net)
    return nets;
  } else {
    return [];
  }
}

function getCompsByPosition(x, y) {
  var element = document.elementFromPoint(x, y);
  if (element.tagName !== 'svg') {
    select(element).attr('display', 'none');
    var comps = getCompsByPosition(x, y);
    select(element).attr('display', null);
    var comp = element.__data__.comp;
    if (comp && comps.indexOf(comp) < 0)
      comps.push(comp)
    return comps;
  } else {
    return [];
  }
}

CompLayerRenderer.prototype.redraw = function () {
  var self = this;

  this.d3CompGroup = this.layerElement
    .selectAll('g')
    .data(this.compObjList)
    .enter()
    .append('g')
    .attr('comp', function (compObj) {
      return compObj.name;
    });
  //add rect (rocky cpm)
  this.d3CompGroup
    .append("rect")
    .attr("id", function (compObj) {
      return `${compObj.name}-rect`;
    });

  this.updateColoring();

  const mComponentsList = this.metalLayer.mComponentLayer.mComponents;
  this.d3CompGroup
    .data(this.compObjList)
    .each(function (compObj) {
      if (compObj.outlineObj)
        compObj.outlineObj.drawSVG(select(this), {
          'fill-opacity': 0
        });
      compObj.objList.drawSVG(select(this));
      compObj.svgGrp = this;
      select(this).on('click', function (d) {
        clickEventHandling(d, self.layoutRenderer, self.clickCallback);
      })
    })
    .append('title')
    .text(function (compObj, index) {
      let x = null, y = null, compInfo = null;
      if (mComponentsList[index].mName === compObj.name) {
        compInfo = mComponentsList[index];
      } else {
        const findComponentIndex = mComponentsList.findIndex(item => item.mName === compObj.name);
        if (findComponentIndex > -1) {
          compInfo = mComponentsList[findComponentIndex];
        }
      };
      if (compInfo) {
        x = compInfo.mLocation.mPosition.mX;
        y = compInfo.mLocation.mPosition.mY;
      }

      return `${compObj.name}\nLayer: ${compObj.outlineObj.layer}\n(${x}, ${y})`
    });

  this.updateStrokeWidth();

  // call the virtual function of the parent
  LayerRenderer.prototype.redraw.call(this);

}; // CompLayerRenderer.prototype.redraw

CompLayerRenderer.prototype.updateColoring = function () {

  // use the default component color
  // var color = this.layoutDB.GetLayoutSettings().GetLayerColor(this.layerName);
  var color = COMPONENT_COLOR;

  if (this.layoutRenderer.fillMode)
    this.layerElement
      .attr('fill', color)
      .attr('stroke', null);
  else
    this.layerElement
      .attr('fill', 'black')
      .attr('fill-opacity', 0)
      .attr('stroke', color);
};

/** virtual function - set the object visibility of this layer */
CompLayerRenderer.prototype.updateVisibility = function () {

  var hidden = true;
  if (this.layoutDB.GetLayoutSettings().IsLayerVisible(this.layerName)) {
    hidden = null;
    // this layer is now visible, render if it has not been done before
    if (!this.dataLoaded) {
      this.prepareData();
    }
    if (!this.rendered) {
      // $rootScope.$broadcast('layerRendering', this.layerName);
      this.redraw();
      // $rootScope.$broadcast('layerRendered', this.layerName);
    }
  }
  this.layerElement.attr('hidden', hidden);
};

CompLayerRenderer.prototype.setRenderSettings = function (renderSettings) {
  this.renderSettings = { ...renderSettings };
}

/** virtual function - for line shapes, update the line width of the layer */
CompLayerRenderer.prototype.updateStrokeWidth = function () {

  // use the default component color
  // var color = this.layoutDB.GetLayoutSettings().GetLayerColor(this.layerName);
  const { compColor } = this.renderSettings;
  for (var i = 0, len = this.compObjList.length; i < len; i++) {
    if (this.compObjList[i].outlineObj) {
      const isSelected = this.compObjList[i].selected;
      let _color = getCompColorBySettingColor(isSelected, compColor);
      if (this.compObjList[i].selected) {
        const name = this.compObjList[i].name;
        const data = this.compPinsData.find(item => item.comp === name)
        if (data) {
          _color = getCompColor(data.type, _color)
        }
      }
      this.compObjList[i].outlineObj.setStroke(this.layoutRenderer.outlineStrokeWidth, _color);
    }
  }

  if (this.pinOutline)
    this.pinOutline.setStroke(this.layoutRenderer.outlineStrokeWidth, PINPATH_COLOR)

  const gridLines = select(this.layerElement.node().parentNode)
    .select('g.gridLines')
    .selectAll('line')
  if (gridLines.size() > 0) {
    gridLines.attr('stroke-width', this.layoutRenderer.outlineStrokeWidth)
  }
};

/** set or unset the component selection coloring
 *  @param compName name of the component to be changed, note that the component maynot belong
 *                  to this layer
 *  @param select whether to set or unset the selection coloring
 */
CompLayerRenderer.prototype.setCompSelection = function (compNames, isSelected, compPinsData = []) {
  this.compPinsData = compPinsData;
  for (var i = 0, len = this.compObjList.length; i < len; i++) {

    const compObj = this.compObjList[i];
    if (compNames.indexOf(compObj.name) > -1) {
      compObj.selected = isSelected;

      const { compColor } = this.renderSettings;
      var color = getCompColorBySettingColor(isSelected, compColor);
      var fill = null;
      var stroke = null;
      if (isSelected) {
        // when not selected, the color and filling are controlled outside
        fill = this.layoutRenderer.fillMode ? color : 'black';
        stroke = this.layoutRenderer.fillMode ? null : color;
      }

      if (compPinsData.length) {
        const findInfo = compPinsData.find(item => item.comp === compObj.name);
        if (findInfo) {
          const _color = getCompColor(findInfo.type)
          color = _color;
          fill = _color;
        }
      }

      // set the group coloring (for the pins)
      select(compObj.svgGrp)
        .attr('fill', fill)
        .attr('stroke', stroke)
        .attr('opacity', null)
        .attr('fill-opacity', 1)

      // set the outline coloring
      if (compObj.outlineObj && compObj.outlineObj.element) {
        compObj.outlineObj.element.attr('stroke', color);
      }
    } else {
      if (isSelected)
        select(compObj.svgGrp)
          .attr('opacity', 0.2);
      else
        select(compObj.svgGrp)
          .attr('opacity', null);
    }
  }
};

CompLayerRenderer.prototype.setPinSelection = function (pins = {}, isSelected, color = 'yellow') {
  this.setPinAttr(pins, isSelected, 'fill', color);
}

CompLayerRenderer.prototype.setPinOutlineByPinMode = function (pins = {}, isSelected, hollowSvgNode, strokeWidth) {
  const { pinMode, pinFill, pinStroke } = this.renderSettings;
  const comps = Object.keys(pins);
  for (let comp of comps) {
    const find = this.compObjList.find(d => d.name === comp);
    if (!find) continue;
    const pinMap = new Map();
    for (let canvasItem of Object.keys(find.objList).filter(k => Array.isArray(find.objList[k]) && find.objList[k].length)) {
      for (let pinObj of find.objList[canvasItem]) {
        pinMap.set(pinObj.pinNumber, pinObj);
      }
    }

    const compPins = pins[comp];
    if (compPins && compPins.length) {
      for (let pin of compPins) {
        const findPin = pinMap.get(pin);
        if (!findPin) {
          continue;
        }
        select(findPin.element)
          .attr('fill', "none");
        const currElement = findPin.element.cloneNode(true);
        hollowSvgNode.append(function () { return currElement; });
        switch (findPin.shape) {
          case "circle":
          case "ellipse":
            select(currElement)
              .attr("stroke", isSelected ? pinStroke : '')
              .attr("fill", isSelected ? pinFill : '')
              .attr('stroke-width', strokeWidth)
            break;
          case "path":
          case "polyline":
          case "polygon":
            select(currElement)
              .attr("stroke", isSelected ? pinStroke : '')
              .attr("fill", isSelected ? pinFill : '')
              .attr('stroke-width', strokeWidth)
            break;
          default:
            break;
        }

        if (pinMode === 'hollow_ring') {
          const clonedElement = currElement.cloneNode(true);
          hollowSvgNode.append(function () { return clonedElement; });
          const hollowShape = CanvasHollowInner.getHollowInnerData(findPin, 0.5);

          switch (hollowShape.shape) {
            case "circle":
              select(clonedElement)
                .attr('stroke', isSelected ? pinStroke : '')
                .attr('fill', pinStroke)
                .attr('r', hollowShape.r);
              break;
            case "polygon":
              select(clonedElement)
                .attr("fill", pinStroke)
                .attr('points', hollowShape.polyData);
              break;
            case "ellipse":
              select(clonedElement)
                .attr("fill", pinStroke)
                .attr('rx', hollowShape.rx)
                .attr('ry', hollowShape.ry)
              break;
            case "path":
              select(clonedElement)
                .attr("fill", pinStroke)
                .attr('d', hollowShape.pathData)
              break;
            case "polyline":
              select(clonedElement)
                .attr("fill", pinStroke)
                .attr('points', hollowShape.polyData)
                .attr('stroke-width', hollowShape.width)
              break;
            default:
              break;
          }
        }
      }
    }
  }
}

CompLayerRenderer.prototype.highlightPinSelection = function (pins = {}, svgNode) {
  const comps = Object.keys(pins);

  for (let comp of comps) {
    const find = this.compObjList.find(d => d.name === comp)

    if (!find) continue;
    const pinsNode = svgNode.append("g").classed(`comp-pins-selection-g-${comp}`, true);
    find.objList.drawSVG(pinsNode, SELECTION_COLOR, true);
  }
}

CompLayerRenderer.prototype.setPinOpacity = function (pins = {}, opacity = '') {
  //this.setPinAttr(pins, true, 'opacity', opacity);
  const _opacity = opacity ? opacity.replace(".", "-") : "1";
  const className = `pin-opacity-port-${_opacity}`;
  this.setPinAttr(pins, true, 'class', className);
}

CompLayerRenderer.prototype.setPinAttr = function (pins = {}, isSelected, attrName, value) {
  const comps = Object.keys(pins);
  for (let comp of comps) {
    const find = this.compObjList.find(d => d.name === comp);
    if (!find) continue;
    const pinMap = new Map();
    for (let canvasItem of Object.keys(find.objList).filter(k => Array.isArray(find.objList[k]) && find.objList[k].length)) {
      for (let pinObj of find.objList[canvasItem]) {
        pinMap.set(pinObj.pinNumber, pinObj);
      }
    }

    const compPins = pins[comp];
    if (compPins && compPins.length) {
      for (let pin of compPins) {
        const findPin = pinMap.get(pin);
        if (findPin) {
          if (attrName === 'class') {
            select(findPin.element)
              .classed(value, isSelected)
              .on('mousedown', ((canvas) => {
                //right mouse click
                if (event && event.button === 2) {
                  this.layoutRenderer.rightClickPin({ canvas, findPin })
                }
              }))
          } else {
            select(findPin.element)
              .attr(attrName, isSelected ? value : '')
              .on('mousedown', ((canvas) => {
                //right mouse click
                if (event && event.button === 2) {
                  this.layoutRenderer.rightClickPin({ canvas, findPin })
                }
              }))
          }
          continue;
        }
      }
    }
  }
}

CompLayerRenderer.prototype.findPins = function (pins = {}) {
  const comps = Object.keys(pins);
  let arr = [];
  for (let comp of comps) {
    const compPins = pins[comp];
    if (!compPins || !compPins.length) continue;

    const find = this.compObjList.find(d => d.name === comp)
    if (!find) continue;

    const pinMap = {};
    const objList = find.objList;

    for (let key in objList) {
      const items = objList[key];
      if (!Array.isArray(items)) continue;
      for (let j = 0; j < items.length; j++) {
        const pin = items[j];
        if (pin && pin.pinNumber != null) {
          pinMap[pin.pinNumber] = pin;
        }
      }
    }

    for (let j = 0; j < compPins.length; j++) {
      const pinNumber = compPins[j];
      if (pinMap[pinNumber]) {
        arr.push(pinMap[pinNumber]);
      }
    }
  }
  return arr;
}

CompLayerRenderer.prototype.hightlightCompOutline = function (showOutLineList, isSelected) {
  const filterCompObjData = this.compObjList.filter(compObj => showOutLineList.includes(compObj.name))

  for (let compObj of filterCompObjData) {
    compObj.selected = isSelected;
    let color = isSelected ? SELECTION_COLOR : COMPONENT_COLOR;
    let stroke = null;
    if (isSelected) {
      // when not selected, the color and filling are controlled outside
      stroke = this.layoutRenderer.fillMode ? null : color;
    }

    select(compObj.svgGrp)
      .attr('fill', 'yellow')
      .attr('stroke', stroke)
      .attr('opacity', null)
      .attr('fill-opacity', 0.3)
  }
}

CompLayerRenderer.prototype.addColorToPortPins = function ({ ports, direction }) {

  const comps = Object.keys(ports);
  for (let comp of comps) {
    const find = this.compObjList.find(d => d.name === comp)
    if (!find) continue;
    const canvasArr = Object.keys(find.objList).filter(d => Array.isArray(find.objList[d]) && find.objList[d].length);
    const compPins = ports[comp];
    if (compPins && compPins.length) {
      for (let pin of compPins) {
        for (let canvasItem of canvasArr) {
          const findPin = find.objList[canvasItem].find(d => d.pinNumber === pin);
          if (findPin) {
            select(findPin.element)
              .classed(`pin-${direction}-port-box`, true)
            continue;
          }
        }
      }
    }
  }
}

CompLayerRenderer.prototype.getFourPoint = function (compName) {
  return this.point.get(compName)
}

CompLayerRenderer.prototype.getPinPath = function (pins, size, widthSize, heightSize) {
  const compList = this.metalLayer.GetComponentLayer().GetComponents();
  if (!compList) {
    console.log("Unable to get the component layer from metal layer.");
    return;
  }
  for (let i = 0; i < compList.length; i++) {
    const compName = compList[i].GetName();
    const cePart = compList[i].mPart;
    const footPrintSymbol = cePart.GetFootPrintSymbol(this.layoutDB.mPartMgr);
    const pads = footPrintSymbol ? footPrintSymbol.GetPadGeometries() : [];
    let minY, minX = minY = Infinity;
    let maxY, maxX = maxY = -Infinity;
    if (pads) {
      for (let j = 0; j < pads.length; j++) {
        const num = footPrintSymbol.mMetalLayerList[0].mPadList[j].mPadID;
        const pinInfo = cePart.GetPinByPinNumber(num);
        const pinNumber = pinInfo.mNumber;
        if (pins.includes(pinNumber)) {
          if (pads[j] instanceof GCPolygon) {
            minX = Math.min(minX, Math.min.apply(null, pads[j].mXs));
            minY = Math.min(minY, Math.min.apply(null, pads[j].mYs));
            maxX = Math.max(maxX, Math.max.apply(null, pads[j].mXs));
            maxY = Math.max(maxY, Math.max.apply(null, pads[j].mYs));
          } else if (pads[j] instanceof CeCircle) {
            const centerX = pads[j].mCenter.getX();
            const centerY = pads[j].mCenter.getY();
            const r = pads[j].mDiameter / 2;
            minX = Math.min(minX, centerX - r);
            minY = Math.min(minY, centerY - r);
            maxX = Math.max(maxX, centerX + r);
            maxY = Math.max(maxY, centerY + r);
          } else console.log(pads[j])
        }
      }
    }

    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;
    const width = maxX - minX;
    const height = maxY - minY;
    const scaledWidth = width * size * widthSize;
    const scaledHeight = height * size * heightSize;
    minX = centerX - scaledWidth / 2;
    minY = centerY - scaledHeight / 2;
    maxX = centerX + scaledWidth / 2;
    maxY = centerY + scaledHeight / 2;

    let outline = new CePolygon();
    outline.AddVertex(minX, minY);
    outline.AddVertex(minX, maxY);
    outline.AddVertex(maxX, maxY);
    outline.AddVertex(maxX, minY);
    outline = outline.ConvertToGCPolygon();
    if (compList[i].mLocation)
      compList[i].mLocation.TransformGCPolygon(outline);
    this.point.set(compName, outline)
    this.pinOutline = new CanvasPath();
    this.pinOutline.pathData = outline.GetSvgPath();
  }
}

CompLayerRenderer.prototype.redrawPinPath = function (svgNode) {
  this.pinOutline.drawSVG(svgNode, {
    'class': 'layer_pin',
    'fill-opacity': 0
  });
  this.updateStrokeWidth()
}

CompLayerRenderer.prototype.getHiddenComps = function (hiddenComps) {
  hiddenComps.forEach((compName) => {
    this.ele = this.layerElement
      .selectAll(`g[comp="${compName}"]`)
      .attr('display', 'none')
  })
}

CompLayerRenderer.prototype.deleteHiddenComp = function (hiddenComp) {
  this.ele = this.layerElement
    .selectAll(`g[comp="${hiddenComp}"]`)
    .attr('display', 'unset')
}

CompLayerRenderer.prototype.setNotDisplayPin = function (displayPinList) {
  const comps = Object.keys(displayPinList);
  for (let comp of comps) {
    const find = this.compObjList.find(d => d.name === comp)
    if (!find) continue;
    const canvasArr = Object.keys(find.objList).filter(d => Array.isArray(find.objList[d]) && find.objList[d].length);
    const compPins = displayPinList[comp];
    if (compPins && compPins.length) {
      const compPinsSet = new Set(compPins);
      const showElements = [];
      const hideElements = [];
      for (let canvasItem of canvasArr) {
        find.objList[canvasItem].forEach(findPin => {
          if (!findPin || !findPin.element) return;
          if (compPinsSet.has(findPin.pinNumber)) {
            showElements.push(findPin.element);
          } else {
            hideElements.push(findPin.element);
          }
        });
      }
      if (hideElements.length) selectAll(hideElements).classed('canvas-not-display-pin', true);
      if (showElements.length) selectAll(showElements).classed('canvas-not-display-pin', false);
    }
  }
}

function getCompColor(type, _color) {
  switch (type) {
    case 'pullRes':
      return '#00ff82';
    case 'dampRes':
      return '#1ba0e0';
    default:
      return _color || SELECTION_COLOR;
  }
}

function getCompColorBySettingColor(isSelected, settingColor, defaultSelectedColor = SELECTION_COLOR, defaultColor = COMPONENT_COLOR) {
  return settingColor ? settingColor : (isSelected ? defaultSelectedColor : defaultColor);
}

export default CompLayerRenderer;